import React, { useId, useRef, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

export interface SectionNavigationItem {
  id: string;
  label: string;
  /** Omit for state-only sections and handle selection with onSelect. */
  href?: string;
  restricted?: boolean;
}

export interface SectionNavigationGroup {
  label?: string;
  items: ReadonlyArray<SectionNavigationItem>;
}

interface Props {
  currentTab: string;
  groups: ReadonlyArray<SectionNavigationGroup>;
  label: string;
  footer?: React.ReactNode;
  onSelect?: (event: React.MouseEvent<HTMLElement>, id: string) => void;
}

export default function SectionNavigation({ currentTab, groups, label, footer, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string>();
  const statusId = useId();
  const [searchFocused, setSearchFocused] = useState(false);
  const entries = useRef(new Map<string, HTMLElement>());
  const query = search.trim().toLowerCase();
  const populatedGroups = groups.filter((group) => group.items.length > 0);
  const showGroupHeadings = populatedGroups.length > 1;
  const filteredGroups = populatedGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) => `${item.label} ${group.label ?? ''}`.toLowerCase().includes(query)),
  }));
  const orderedItems = filteredGroups.flatMap((group) => group.items);
  const activeItem =
    query && searchFocused ? (orderedItems.find((item) => item.id === activeId) ?? orderedItems[0]) : undefined;

  function handleSearchKeyDown(event: React.KeyboardEvent) {
    if (!query || !orderedItems.length || event.nativeEvent.isComposing) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (activeItem) {
        entries.current.get(activeItem.id)?.click();
      }
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const currentIndex = activeItem ? orderedItems.indexOf(activeItem) : 0;
      const nextIndex = Math.max(
        0,
        Math.min(orderedItems.length - 1, currentIndex + (event.key === 'ArrowDown' ? 1 : -1)),
      );
      setActiveId(orderedItems[nextIndex].id);
      entries.current.get(orderedItems[nextIndex].id)?.scrollIntoView({ block: 'nearest' });
    }
  }

  return (
    <Box
      component="nav"
      aria-label={label}
      sx={{
        position: { md: 'sticky' },
        top: 16,
        maxHeight: { xs: '50vh', md: 'calc(100vh - 32px)' },
        overflowY: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1,
      }}
    >
      <TextField
        label="Find a section"
        size="small"
        fullWidth
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setActiveId(undefined);
        }}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        onKeyDown={handleSearchKeyDown}
        inputProps={{ 'aria-describedby': statusId }}
        sx={{ mb: 1 }}
      />
      <Box id={statusId} role="status" sx={visuallyHidden}>
        {query &&
          searchFocused &&
          (activeItem
            ? `${orderedItems.length} matching sections. ${activeItem.label}. Use up and down arrows to choose a section, then Enter to open it.`
            : 'No matching sections.')}
      </Box>
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: '1fr' }, gap: 1 }}
      >
        {filteredGroups.map((group, groupIndex) => {
          const groupItems = group.items;
          if (!groupItems.length) {
            return null;
          }

          return (
            <Box key={group.label ?? groupIndex}>
              {showGroupHeadings && group.label && (
                <Typography variant="overline" component="div" color="primary.main" sx={{ px: 1.5, fontWeight: 700 }}>
                  {group.label}
                </Typography>
              )}
              <List disablePadding>
                {groupItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    ref={(element: HTMLElement | null) => {
                      if (element) {
                        entries.current.set(item.id, element);
                      } else {
                        entries.current.delete(item.id);
                      }
                    }}
                    {...(item.href
                      ? { component: Link, to: item.href }
                      : { component: 'button' as const, type: 'button' as const })}
                    selected={currentTab === item.id}
                    aria-current={currentTab === item.id ? 'page' : undefined}
                    onClick={(event) => onSelect?.(event, item.id)}
                    sx={{
                      bgcolor: activeItem?.id === item.id ? 'action.hover' : undefined,
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      color: item.restricted ? 'error.main' : undefined,
                      '&.Mui-selected': { color: 'primary.dark' },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: currentTab === item.id ? 600 : 400 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>
      {!orderedItems.length && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
          No matching sections
        </Typography>
      )}
      {footer}
    </Box>
  );
}
